import type { Rec } from "@shared/types.js";

export interface FilterState {
  comps: Set<string>;
  cats: Set<string>;
  region: string;
  status: string;
  rateType: string;
  pic: string;
  search: string;
  includeCatE: boolean;
}

export const emptyFilters = (): FilterState => ({
  comps: new Set(), cats: new Set(),
  region: "", status: "", rateType: "", pic: "", search: "",
  includeCatE: false,
});

export const STATUS_OPTIONS: [string, string][] = [
  ["", "All statuses"],
  ["received", "CR received"],
  ["notreceived", "CR not received"],
  ["priced", "New Rate entered"],
  ["awaiting", "Received, awaiting pricing"],
  ["estimated", "Estimation entered"],
  ["estout", "Estimation outstanding"],
  ["crosschecked", "Crosschecked"],
  ["notcrosschecked", "Entered, not crosschecked"],
  ["exported", "Exported"],
  ["notexported", "Not exported"],
];

export function statusMatch(r: Rec, st: string): boolean {
  switch (st) {
    case "received": return r.received;
    case "notreceived": return !r.received;
    case "priced": return r.nrDone;
    case "awaiting": return r.received && !r.nrDone;
    case "estimated": return r.estDone;
    case "estout": return !r.received && !r.estDone;
    case "crosschecked": return !!r.ccDate;
    case "notcrosschecked": return (r.nrDone || r.estDone) && !r.ccDate;
    case "exported": return !!r.exDate;
    case "notexported": return !r.exDate;
    default: return true;
  }
}

/**
 * Cat. E is a physically separate sheet whose suppliers are mostly NOT in the
 * Accommodation sheet. The workbook's own Overview tab ignores it, so it is
 * excluded by default and controlled by a toggle.
 */
export function applyFilters(all: Rec[], f: FilterState): Rec[] {
  const q = f.search.trim().toLowerCase();
  return all.filter((r) => {
    if (r.comp === "cat_e" && !f.includeCatE) return false;
    if (f.comps.size) {
      const key = r.comp === "cat_e" ? "accommodation" : r.comp;
      if (!f.comps.has(key)) return false;
    }
    if (f.cats.size && (!r.cat || !f.cats.has(r.cat))) return false;
    if (f.region && r.region !== f.region) return false;
    if (f.rateType && (r.rateType ?? "").toLowerCase() !== f.rateType.toLowerCase()) return false;
    if (f.pic && r.nrPic !== f.pic && r.estPic !== f.pic && r.ccBy !== f.pic) return false;
    if (f.status && !statusMatch(r, f.status)) return false;
    if (q) {
      const hay = `${r.name ?? ""} ${r.code ?? ""} ${r.town ?? ""} ${r.product ?? ""} ${r.region ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function isDefault(f: FilterState): boolean {
  return f.comps.size === 0 && f.cats.size === 0 && !f.region && !f.status
    && !f.rateType && !f.pic && !f.search.trim() && !f.includeCatE;
}

/** Distinct non-empty values for a select, sorted. */
export function uniqValues(rows: Rec[], keys: (keyof Rec)[]): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "string" && v.trim()) set.add(v.trim());
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}
